import { useEffect, useLayoutEffect } from 'react'
import { Link, useParams } from 'react-router'
import { IconArrowLeft, Text } from '@n26/nxd'
import { ROUTES } from '@/shared/routes'
import {
  leaveForHomeFrom,
  takeReturn,
} from '@/features/dashboard/model/homeReturn'
import { sentenceCase } from '@/features/dashboard/format/format'
import { useDashboardStructure } from '@/features/dashboard/api/queries'
import { DeepDiveFilters } from '@/features/dashboard/filters/DeepDiveFilters'
import { useDeepDiveMarkets } from '@/features/dashboard/filters/useSectionMarkets'
import { ExpandedModule } from '@/features/dashboard/tiles/ExpandedModule'
import { useViewAsTable } from '@/features/dashboard/filters/useViewAsTable'
import { deepDiveById, homeCardOf } from '@/features/dashboard/model/sections'
import { CardGroup } from './CardGroup'
import { Loading } from './DeepDiveCard'
import { DeepDiveHeader } from './DeepDiveHeader'
import { groupsWithout, hasChartCards } from './deepDiveLayout'
import styles from './DeepDivePage.module.css'

export const DeepDivePage = () => {
  const { sectionId } = useParams()
  const { data: dashboard, isPending, isError } = useDashboardStructure()
  const markets = useDeepDiveMarkets(sectionId)
  const { asTable } = useViewAsTable()
  // A deep dive opens at the top, not where the page it came from was scrolled to.
  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [sectionId])
  // Whichever way the user leaves for home, the home page scrolls to the card that linked here.
  useEffect(() => {
    if (!sectionId) return
    leaveForHomeFrom(sectionId)
    return () => {
      // Read at unmount: the router's location is still this page's here.
      if (window.location.pathname !== ROUTES.HOME) takeReturn()
    }
  }, [sectionId])

  if (isPending) return <Loading />
  if (isError) {
    return (
      <div role='alert'>
        <Text color='rhubarb'>Couldn&apos;t load the dashboard.</Text>
      </div>
    )
  }

  const section = deepDiveById(dashboard.sections, sectionId)
  // The headline is the home page's expanded card, so the deep dive's own copy is left out.
  const home = section ? homeCardOf(dashboard.sections, section.id) : undefined
  const groups = section ? groupsWithout(section, home?.stub.cardId) : []
  return (
    <div className={styles.page}>
      <DeepDiveHeader
        home={home}
        viewToggle={hasChartCards(groups, home)}
        markets={markets}
      >
        <Link
          to={ROUTES.HOME}
          className={styles.back}
          aria-label='Back to dashboard'
        >
          <IconArrowLeft aria-hidden />
        </Link>
        <h1 className={styles.title}>
          {section ? sentenceCase(section.title) : 'Deep dive'}
        </h1>
      </DeepDiveHeader>
      {section ? (
        <>
          <DeepDiveFilters section={section} />
          {home && (
            <div className={styles.headline}>
              <ExpandedModule
                key={home.stub.cardId}
                stub={home.stub}
                section={home.section}
                embedded
              />
            </div>
          )}
          {groups.map((group, index) => (
            <CardGroup
              key={`${index}-${group.key}`}
              group={group}
              deepDiveId={section.id}
              asTable={asTable}
            />
          ))}
        </>
      ) : (
        <Text>Deep dive not found.</Text>
      )}
    </div>
  )
}
